/**
 * @packageDocumentation
 * @module f1/database
 */

import { t_lens, t_key, t_pointer } from '../types';
import is_undefined from '../is/is_undefined';
import is_function from '../is/is_function';
import is_string from '../is/is_string';
import is_array from '../is/is_array';
import lens_fn from './lens';

/** @ignore is only numbers */
const isN = /^\d+$/;

/** @ignore */
type t_write_update_fn<V> = (v: V, parent: Object) => V;

/**
 * @param obj the object to write/read to
 */
export type WriteValue<V> = (obj: Object) => Object;

export interface Write {
  /**
   * Write a value to an object or use a function to write the value
   *
   * @public
   * @see f1.lens uses a lens to write from an object
   * @function f1.write
   * @param lens a lens to look in to the object with
   * @param update a value or function to write a value
   * @param obj the object to write/read to
   * @example
   *
   * ```ts
   * //
   * f1.write(f1.lens('/p1'), 2, { p1: 1 }) === { p1: 2 }
   * // add one to the value itself
   * f1.write(f1.lens('/p1/1'), (v) => v + 1, { p1: [2, 4] }) === { p1: [2, 5] }
   * ```
   */
  <V>(lens: t_pointer | t_lens, update: V | t_write_update_fn<V>, obj: Object): Object;
  /**
   * Write a value to an object or use a function to write the value
   *
   * @public
   * @see f1.lens uses a lens to write from an object
   * @function f1.write
   * @param lens a lens to look in to the object with, or the pointer which will be converted to a lens
   * @param update a value or function to write a value
   * @example
   *
   * ```ts
   * //
   * const write2ToP1 = f1.write(f1.lens('/p1'), 2);
   * // invoke the function
   * write2ToP1({ p1: 1 }) === { p1: 2 }
   * ```
   */
  <V>(lens: t_pointer | t_lens, update: V | t_write_update_fn<V>): WriteValue<V>;
}
/**
 * @see Write
 */
export default <V>(lens: t_pointer | t_lens, update: V | t_write_update_fn<V>, obj: Object): Object => {
  lens = is_string(lens) ? lens_fn(lens) : lens;

  const parent = obj;
  let part: t_key = '';
  let hasNextPart;
  let isArrRef;
  let nextP;

  const n = lens.length;
  let p = 1;

  // an empty lens should return the parent
  if (n === 1) {
    return parent;
  }

  // descend to location
  for (; p < n; ) {
    part = lens[p++];
    hasNextPart = n > p;

    if (part === '') {
      continue;
    } else if (is_undefined(obj[part])) {
      if (is_array(obj) && part === '-') {
        part = obj.length;
      }

      if (hasNextPart) {
        nextP = lens[p];
        // ? (using one variable for performance)
        // 1. a dash indicates add to an array
        // 2. isN is to check if we have a numeric index, then we create an array
        isArrRef = nextP === '-' || nextP.match(isN) != null;
        obj[part] = isArrRef ? [] : {};
      }
    }

    if (!hasNextPart) {
      break;
    }

    obj = obj[part];
  }

  // write or delete value
  if (is_undefined(update)) {
    // @todo do we copy entire object without this one part?
    delete obj[part];
  } else {
    obj[part] = is_function(update) ? update(obj[part], obj) : update;
  }

  return parent;
};
